import { AttachmentsViewerModal } from '@components/Attachments'
import { loadErrorMessage } from '@lib/apiError'
import { BookingMembershipButton } from '@components/Chat'
import { flightGroupCard } from '@components/Chat/core'
import BackLink from '@components/BackLink'
import { countLabel } from '@/utils/plural'
import ConfirmationModal from '@components/ConfirmationModal'
import PageLoader from '@components/PageLoader'
import { useAuth } from '@context/AuthContext'
import { useDeleteFlightGroup, useFlights, useTrip, useTripMembers } from '@hooks'
import { normalizeExternalUrl } from '@lib/utils'
import { FlightResponse, GetFlightGroupResponse, GetUserType } from '@orbit/api-types'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import EditFlightGroupModal from '../EditFlightGroupModal'
import EditLegModal from '../EditLegModal'
import AddFlightModal from '../AddFlightModal'
import {
	getArrivalDisplay,
	getDepartureDisplay,
	getDurationString,
	getFlightCode,
	getLayoverString,
} from '../flightHelpers'
import { FlightIcon, PlusIcon } from '@/assets/icons'
import {
	ActionButtons,
	AddLegCallout,
	AttachmentButton,
	AvatarStack,
	DetailBody,
	DetailCard,
	DetailHeader,
	DetailTitle,
	Discussion,
	IconButton,
	LayoverBanner,
	LegCard,
	LegCode,
	LegCountBadge,
	LegEditHint,
	LegMeta,
	LegPassengerRow,
	LegRouteAirport,
	LegRouteArrow,
	LegRouteBlock,
	LegRouteEndpoint,
	LegTimeBlock,
	LegTimeLabel,
	LegTimeValue,
	LegsContainer,
	LinkItem,
	LinksList,
	LoadingContainer,
	ManualPill,
	MembershipRow,
	MetaSection,
	NotFoundMessage,
	NotesContent,
	StackedAvatar,
	PassengerCountText,
	SectionLabel,
	TitleRow,
	YouPill,
} from './FlightDetailPage.styles'
import { iconSizes } from '@/theme/tokens'
import { ErrorNote } from '@components/Notice'
import { PageColumn, PageScroll } from '@components/Page'
import { TestIds } from '@/testIds'

const MAX_VISIBLE_AVATARS = 5


const getMemberName = (member: GetUserType): string => {
	return [member.firstName, member.lastName].filter(Boolean).join(' ')
}

const getLegPassengerIds = (leg: FlightResponse): number[] => {
	return leg.passengers.map(p => p.userId)
}

const getGroupPassengerIds = (group: GetFlightGroupResponse): number[] => {
	const ids = new Set<number>()
	for (const leg of group.flights) {
		for (const p of leg.passengers) {
			ids.add(p.userId)
		}
	}
	return Array.from(ids)
}

const FlightDetailPage: React.FC = () => {
	const { tripId, flightGroupId } = useParams<{ tripId: string; flightGroupId: string }>()
	const navigate = useNavigate()
	const { user } = useAuth()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: flightsData, isLoading: flightsLoading, error: flightsError } = useFlights(tripId)
	const { data: membersData } = useTripMembers(tripId)

	const deleteFlightGroup = useDeleteFlightGroup()

	const [editingLeg, setEditingLeg] = useState<FlightResponse | null>(null)
	const [isEditGroupOpen, setIsEditGroupOpen] = useState(false)
	const [isDeleteGroupOpen, setIsDeleteGroupOpen] = useState(false)
	const [isAddLegOpen, setIsAddLegOpen] = useState(false)
	const [isAttachmentsViewerOpen, setIsAttachmentsViewerOpen] = useState(false)

	const members = membersData?.members ?? []
	const group = flightsData?.flightGroups?.find(g => g.id === Number(flightGroupId))

	const getMemberById = (userId: number): GetUserType | undefined => {
		return members.find(m => m.id === userId)
	}

	const handleDeleteGroup = () => {
		if (!tripId || !group) return
		deleteFlightGroup.mutate(
			{ tripId, flightGroupId: group.id },
			{
				onSuccess: () => {
					toast.success('Flight deleted')
					navigate(`/trips/${tripId}/flights`)
				},
				onError: () => {
					toast.error('Failed to delete flight. Please try again.')
				},
			}
		)
	}

	if (tripLoading || flightsLoading) {
		return (
			<PageLoader label="Loading flight..." />
		)
	}

	if (flightsError) {
		return <ErrorNote>{loadErrorMessage('flight', flightsError)}</ErrorNote>
	}

	if (!trip || !group) {
		return (
			<LoadingContainer>
				<NotFoundMessage>
					Flight not found
				</NotFoundMessage>
			</LoadingContainer>
		)
	}

	const firstLeg = group.flights[0]
	const hasManual = group.flights.some(l => l.isManuallyAdded)
	const links = group.links ?? []
	const fileIds = group.fileIds ?? []
	const createdBy = getMemberById(group.createdByUserId)
	const groupPassengerIds = getGroupPassengerIds(group)
	const isUserPassenger = user ? groupPassengerIds.includes(user.id) : false

	const titleCode = firstLeg ? getFlightCode(firstLeg) : 'Flight'

	const canDeleteLeg = group.flights.length > 1

	return (
		<PageScroll>
			<PageColumn>
				<DetailCard>
					<DetailHeader>
						<TitleRow>
							<BackLink path={`/trips/${tripId}/flights`} label="Flights" />
							<DetailTitle>
								{titleCode}
								{group.flights.length > 1 && (
									<LegCountBadge>
										+ {group.flights.length - 1} leg{group.flights.length - 1 === 1 ? '' : 's'}
									</LegCountBadge>
								)}
								{hasManual && <ManualPill>Manual</ManualPill>}
								{isUserPassenger && <YouPill>You're a passenger</YouPill>}
							</DetailTitle>
						</TitleRow>
						<ActionButtons>
							<IconButton onClick={() => setIsEditGroupOpen(true)} title="Edit" data-testid={TestIds.flight.editButton}>
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
									<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
								</svg>
							</IconButton>
							<IconButton
								$variant="danger"
								onClick={() => setIsDeleteGroupOpen(true)}
								title="Delete"
								data-testid={TestIds.flight.deleteButton}
							>
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<polyline points="3 6 5 6 21 6" />
									<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
								</svg>
							</IconButton>
						</ActionButtons>
					</DetailHeader>

					<DetailBody>
						<div>
							<SectionLabel>Legs ({group.flights.length})</SectionLabel>
							<LegsContainer>
								{group.flights.map((leg, index) => {
									const prevLeg = index > 0 ? group.flights[index - 1] : null
									const layover = prevLeg
										? getLayoverString(prevLeg.arrivalTime, leg.departureTime)
										: null
									const depStr = getDepartureDisplay(leg)
									const arrStr = getArrivalDisplay(leg)
									const depCode = leg.departureAirportCode ?? leg.departureAirport?.iataCode ?? 'N/A'
									const arrCode = leg.arrivalAirportCode ?? leg.arrivalAirport?.iataCode ?? 'N/A'
									const passengerIds = getLegPassengerIds(leg)
									const visibleIds = passengerIds.slice(0, MAX_VISIBLE_AVATARS)
									const overflowCount = passengerIds.length - MAX_VISIBLE_AVATARS
									return (
										<React.Fragment key={leg.id}>
											{layover && <LayoverBanner>{layover}</LayoverBanner>}
											<LegCard
												$isManual={leg.isManuallyAdded}
												onClick={() => setEditingLeg(leg)}
											>
												<LegCode>{getFlightCode(leg)}</LegCode>

												<LegRouteBlock>
													<LegRouteEndpoint $align="start">
														<LegRouteAirport>{depCode}</LegRouteAirport>
														<LegTimeBlock>
															<LegTimeLabel>Departs</LegTimeLabel>
															<LegTimeValue>{depStr ?? 'N/A'}</LegTimeValue>
														</LegTimeBlock>
													</LegRouteEndpoint>
													<LegRouteArrow>
														<FlightIcon sx={{ fontSize: iconSizes.lg }} />
													</LegRouteArrow>
													<LegRouteEndpoint $align="end">
														<LegRouteAirport>{arrCode}</LegRouteAirport>
														<LegTimeBlock>
															<LegTimeLabel>Arrives</LegTimeLabel>
															<LegTimeValue>{arrStr ?? 'N/A'}</LegTimeValue>
														</LegTimeBlock>
													</LegRouteEndpoint>
												</LegRouteBlock>

												{leg.durationMinutes != null && (
													<LegMeta>
														<span>Duration · {getDurationString(leg.durationMinutes)}</span>
													</LegMeta>
												)}

												<LegPassengerRow>
													<AvatarStack>
														{visibleIds.map((userId, idx) => {
															const member = getMemberById(userId)
															if (!member) return null
															return (
																<StackedAvatar key={userId} $index={idx} user={member} size="sm" ring />
															)
														})}
														{overflowCount > 0 && (
															<StackedAvatar $index={MAX_VISIBLE_AVATARS} size="sm" ring tone="gold" label={`+${overflowCount}`} />
														)}
													</AvatarStack>
													<PassengerCountText>
														{countLabel(passengerIds.length, 'passenger')}
													</PassengerCountText>
													<LegEditHint>Edit</LegEditHint>
												</LegPassengerRow>
											</LegCard>
										</React.Fragment>
									)
								})}
								<AddLegCallout type="button" onClick={() => setIsAddLegOpen(true)}>
									<PlusIcon sx={{ fontSize: iconSizes.sm }} />
									Add Another Leg
								</AddLegCallout>
							</LegsContainer>
						</div>

						<MembershipRow>
							<BookingMembershipButton
								tripId={tripId!}
								kind="flight"
								bookingId={group.id}
								peopleIds={groupPassengerIds}
								myUserId={user?.id}
								members={members}
							/>
						</MembershipRow>

						{links.length > 0 && (
							<div>
								<SectionLabel>Links ({links.length})</SectionLabel>
								<LinksList>
									{links.map((link, index) => (
										<LinkItem key={index} href={normalizeExternalUrl(link)} target="_blank" rel="noopener noreferrer">
											{link}
										</LinkItem>
									))}
								</LinksList>
							</div>
						)}

						{group.notes && (
							<div>
								<SectionLabel>Notes</SectionLabel>
								<NotesContent data-testid={TestIds.flight.notes}>{group.notes}</NotesContent>
							</div>
						)}

						{fileIds.length > 0 && (
							<div>
								<SectionLabel>Attachments ({fileIds.length})</SectionLabel>
								<AttachmentButton type="button" onClick={() => setIsAttachmentsViewerOpen(true)}>
									View {countLabel(fileIds.length, 'attachment')}
								</AttachmentButton>
							</div>
						)}

						<MetaSection>
							{createdBy && <span>Added by {getMemberName(createdBy)}</span>}
						</MetaSection>
					</DetailBody>
				</DetailCard>

				<Discussion tripId={tripId!} resource={flightGroupCard(group, members)} />
			</PageColumn>

			{editingLeg && (
				<EditLegModal
					isOpen={!!editingLeg}
					onClose={() => setEditingLeg(null)}
					tripId={tripId!}
					leg={editingLeg}
					members={members}
					canDelete={canDeleteLeg}
				/>
			)}

			<EditFlightGroupModal
				isOpen={isEditGroupOpen}
				onClose={() => setIsEditGroupOpen(false)}
				onDelete={() => {
					setIsEditGroupOpen(false)
					setIsDeleteGroupOpen(true)
				}}
				group={group}
			/>

			<ConfirmationModal
				isOpen={isDeleteGroupOpen}
				onClose={() => setIsDeleteGroupOpen(false)}
				onConfirm={handleDeleteGroup}
				title="Delete Flight"
				message="Are you sure you want to delete this flight and all its legs? This cannot be undone."
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>

			<AddFlightModal
				isOpen={isAddLegOpen}
				onClose={() => setIsAddLegOpen(false)}
				members={members}
				initialFlightGroup={group}
				tripStartDate={trip.startDate}
			/>

			{tripId && (
				<AttachmentsViewerModal
					isOpen={isAttachmentsViewerOpen}
					onClose={() => setIsAttachmentsViewerOpen(false)}
					tripId={tripId}
					fileIds={fileIds}
					title="Attachments"
					onEditClick={() => setIsEditGroupOpen(true)}
				/>
			)}
		</PageScroll>
	)
}

export default FlightDetailPage
